/**
 * ToolPageShell — the public chrome shared by every /tools page.
 *
 * Editorial treatment matching the Antero Trail portfolio look: navy chrome
 * (header/footer) that blends into the full-bleed dusk hero, a warm cream
 * body in light mode (token dark surface in dark mode), Cormorant Garamond
 * display type, JetBrains Mono eyebrows/labels, gold/amber accents. The
 * signature dimension-line divider marks every content section.
 *
 * Pass the page hero via the `hero` prop so it renders full-bleed between
 * the header and the constrained content column.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

interface ToolPageShellProps {
  children: ReactNode;
  /** Full-bleed hero (ToolHero) rendered above the content column. */
  hero?: ReactNode;
}

/** The 6 calculators — single source for header nav, footer links, index. */
export const TOOL_LINKS = [
  { title: 'Crane Cost Estimator', to: '/tools/crane-cost-estimator' },
  { title: 'Scaffolding Cost Calculator', to: '/tools/scaffolding-cost-calculator' },
  { title: 'Aerial Lift Cost Calculator', to: '/tools/aerial-lift-cost-calculator' },
  { title: 'Water Mitigation Calculator', to: '/tools/water-mitigation-calculator' },
  { title: 'Drywall Repair Calculator', to: '/tools/drywall-repair-calculator' },
  { title: 'Debris & Dumpster Calculator', to: '/tools/dumpster-weight-calculator' },
];

/**
 * DimensionDivider — blueprint-style section marker: |—— LABEL ——|
 * Use it above every content section instead of a bare heading.
 */
export function DimensionDivider({ label }: { label: string }) {
  return (
    <div aria-hidden className="flex items-center gap-3">
      <span className="h-2.5 w-px bg-border" />
      <span className="h-px flex-1 bg-border" />
      <span className="shrink-0 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
        {label}
      </span>
      <span className="h-px flex-1 bg-border" />
      <span className="h-2.5 w-px bg-border" />
    </div>
  );
}

/** Mono eyebrow used above section titles and card titles. */
export function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber-600 dark:text-amber-500">
      {children}
    </p>
  );
}

/** Serif display section heading (the editorial voice of these pages). */
export function DisplayHeading({ children }: { children: ReactNode }) {
  return (
    <h2 className="font-display text-[2.1rem] font-semibold leading-tight tracking-tight text-foreground">
      {children}
    </h2>
  );
}

export default function ToolPageShell({ children, hero }: ToolPageShellProps) {
  return (
    <div className="flex min-h-screen flex-col bg-[#faf6ee] text-foreground dark:bg-background">
      <header className="bg-[#0b1b33] text-white">
        <div className="mx-auto flex w-full max-w-5xl items-center justify-between gap-4 px-4 py-3.5">
          <Link to="/tools" className="flex items-center gap-2.5">
            <span aria-hidden className="h-5 w-1 bg-amber-500" />
            <span className="font-ui font-bold tracking-tight">Antero Trail</span>
            <span className="hidden font-mono text-[11px] uppercase tracking-[0.18em] text-slate-400 sm:inline">
              Field tools
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <Link
              to="/tools"
              className="hidden text-sm text-slate-300 hover:text-white sm:inline"
            >
              All calculators
            </Link>
            <Link
              to="/signup"
              className="rounded-md bg-amber-500 px-3 py-1.5 text-sm font-semibold text-[#12294a] transition-colors hover:bg-amber-400"
            >
              Try the full app
            </Link>
          </div>
        </div>
      </header>

      {hero}

      <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-12">
        {children}
      </main>

      <footer className="bg-[#0b1b33] text-slate-300">
        <div className="mx-auto w-full max-w-5xl px-4 py-10">
          <div aria-hidden className="flex items-center gap-3">
            <span className="h-2.5 w-px bg-slate-600" />
            <span className="h-px flex-1 bg-slate-700" />
            <span className="shrink-0 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-400">
              Free estimating calculators
            </span>
            <span className="h-px flex-1 bg-slate-700" />
            <span className="h-2.5 w-px bg-slate-600" />
          </div>
          <nav
            aria-label="All calculators"
            className="mt-5 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-3"
          >
            {TOOL_LINKS.map((t) => (
              <Link key={t.to} to={t.to} className="hover:text-white">
                {t.title}
              </Link>
            ))}
          </nav>
          <p className="mt-7 text-xs leading-relaxed text-slate-400">
            These calculators produce planning estimates from published public
            rate schedules. They are not a formal quote. Confirm equipment,
            access, and pricing with a licensed operator or rental yard before
            you rely on any number for an estimate.
          </p>
          <p className="mt-3 text-xs text-slate-400">
            Built by{' '}
            <a
              href="https://anterotrail.com"
              className="underline underline-offset-2 hover:text-white"
            >
              Antero Trail
            </a>{' '}
            — makers of AI Estimate Assistant.{' '}
            <Link
              to="/signup"
              className="underline underline-offset-2 hover:text-white"
            >
              Create a free account
            </Link>
            .
          </p>
        </div>
      </footer>
    </div>
  );
}
